import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaSearch, FaSyncAlt } from 'react-icons/fa'
import type { ChartData, ChartOptions } from 'chart.js'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import api from '../../../../helpers/apiRequester'
import { CollapsibleSection } from '../../omnibusShared/collapsibleSection'
import styles from '../shared/mhlShared.module.scss'
import { SourceTabs, StatTile } from '../shared/SourceTabs'
import { LeadsTable } from '../shared/LeadsTable'
import type { AgentStatRow, LeadSource, MhlLead, MhlOverviewSummary, MhlTeam } from '../shared/mhlTypes'
import { useMhlAccess } from '../shared/useMhlAccess'
import { useMhlScope } from '../shared/useMhlScope'
import { alpha, colors } from '../../../../ui'
import { ToggleInput } from '../components/ToggleInput/ToggleInput'
import {
    filterAndSortMhlLeads,
    MHL_LEAD_FILTER_DEFAULTS,
    mhlChannelOptions,
    MhlLeadsFilter,
    type MhlLeadFilterState,
} from './MhlLeadsFilter'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

type Tab = LeadSource | 'all'
type AgentSort = 'total_desc' | 'today_desc' | 'name_asc' | 'signed_desc'

// Webform leads before today are the April import cohort (and older drip),
// not live signups — keep them out of the webform tab.
const WEBFORM_COHORT_FILTER_ENABLED = true

const dayKey = (value: string | null | undefined) => (value ? value.split('T')[0] : '')

const startOfToday = () => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
}

type ChartRangeDays = '10' | '30' | '90'

const CHART_RANGE_OPTIONS: { value: ChartRangeDays; label: string }[] = [
    { value: '10', label: 'Last 10 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
]

const daysInRange = (dayCount: number) => {
    const days: string[] = []
    for (let i = dayCount - 1; i >= 0; i--) {
        const d = new Date()
        d.setDate(d.getDate() - i)
        days.push(d.toISOString().split('T')[0])
    }
    return days
}

/**
 * The server aggregates every webform lead, including the April import, so the
 * cohort tabs build their own figures from the rows they actually show.
 */
const summariseLeads = (rows: MhlLead[]): MhlOverviewSummary => {
    const today = new Date().toISOString().split('T')[0]
    const signed = rows.filter((l) => l.esign.signed)
    return {
        leadsTotal: rows.length,
        leadsTotalToday: rows.filter((l) => dayKey(l.submitted_at) === today).length,
        submittedViaDashboard: rows.filter((l) => l.submitted_via === 'dashboard').length,
        submittedViaWebform: rows.filter((l) => l.submitted_via !== 'dashboard').length,
        aboveQuantumTotal: rows.filter((l) => l.quantum === 'above').length,
        belowQuantumTotal: rows.filter((l) => l.quantum === 'below').length,
        tbcQuantumTotal: rows.filter((l) => !l.quantum).length,
        awaitingReview: rows.filter((l) => l.review_status === 'awaiting_review').length,
        approved: rows.filter((l) => l.review_status === 'approved').length,
        rejected: rows.filter((l) => l.review_status === 'rejected').length,
        // Webform clients get their e-sign link at lead time and never stamp
        // link_sent_at, so an existing campaign counts as sent.
        cfaSent: rows.filter((l) => (l.submitted_via === 'dashboard' ? !!l.esign.link_sent_at : !!l.esign.status))
            .length,
        cfaSigned: signed.length,
        cfaSignedToday: signed.filter((l) => dayKey(l.esign.signature_date) === today).length,
    }
}

const chartFromLeads = (rows: MhlLead[], dayCount: number): ChartData<'line'> => {
    const days = daysInRange(dayCount)
    return {
        labels: days,
        datasets: [
            { label: 'Leads Submitted', data: days.map((d) => rows.filter((l) => dayKey(l.submitted_at) === d).length) },
            {
                label: 'CFA Signed',
                data: days.map(
                    (d) => rows.filter((l) => l.esign.signed && dayKey(l.esign.signature_date) === d).length,
                ),
            },
        ],
    }
}

interface OverviewResponse {
    source: LeadSource | null
    summary: MhlOverviewSummary
    chartData: Record<string, ChartData<'line'>>
}

const CHART_OPTIONS: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'bottom',
            labels: { boxWidth: 10, font: { family: 'Poppins', size: 11 }, color: colors.ink3 },
        },
        tooltip: { mode: 'index', intersect: false },
    },
    scales: {
        x: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: colors.ink4 } },
        y: {
            beginAtZero: true,
            ticks: { precision: 0, font: { family: 'Poppins', size: 11 }, color: colors.ink4 },
            grid: { color: colors.lineSoft },
        },
    },
}

// Restyle the backend's dataset colours to the muted palette.
const restyle = (data: ChartData<'line'> | undefined): ChartData<'line'> | null => {
    if (!data) return null
    const palette = [colors.accent, colors.ink]
    return {
        ...data,
        datasets: data.datasets.map((ds, i) => ({
            ...ds,
            borderColor: palette[i % palette.length],
            backgroundColor: i === 1 ? alpha(colors.ink, 0.06) : alpha(colors.accent, 0.08),
            pointRadius: 3,
            pointBackgroundColor: palette[i % palette.length],
            borderWidth: 2,
            tension: 0.3,
            fill: i === 1,
        })),
    }
}

const TAB_HINT: Record<Tab, string> = {
    dashboard:
        'Clients onboarded by MHL agents through this dashboard. These go through review before the e-sign link is released.',
    webform:
        'Clients who signed themselves up through the public Military Hearing Loss webform. These skip agent review and go straight to e-sign.',
    all: 'Every Military Hearing Loss lead regardless of how it was captured.',
}

export const MilitaryHearingLossLeadTracker = () => {
    const navigate = useNavigate()
    const { isMhlAdmin } = useMhlAccess()
    const { scope, loading: scopeLoading } = useMhlScope()
    // Team leaders are scoped to their team by the server; admins may opt
    // into a team-based view via the header selector.
    const scoped = !!scope && !scope.all
    const [teams, setTeams] = useState<MhlTeam[]>([])
    const [viewTeamId, setViewTeamId] = useState<'all' | number>('all')
    const teamView = isMhlAdmin && viewTeamId !== 'all' ? (teams.find((t) => t.id === viewTeamId) ?? null) : null
    const [tab, setTab] = useState<Tab>('all')
    const [overviews, setOverviews] = useState<Partial<Record<Tab, OverviewResponse>>>({})
    const [leads, setLeads] = useState<MhlLead[]>([])
    const [agentStats, setAgentStats] = useState<AgentStatRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [forbidden, setForbidden] = useState(false)
    const [leadFilters, setLeadFilters] = useState<MhlLeadFilterState>(MHL_LEAD_FILTER_DEFAULTS)
    const [chartRange, setChartRange] = useState<ChartRangeDays>('10')
    const [agentSearch, setAgentSearch] = useState('')
    const [agentSort, setAgentSort] = useState<AgentSort>('total_desc')

    useEffect(() => {
        if (!isMhlAdmin) return
        api({ url: '/military-hearing-loss-teams-list', data: {} })
            .then((res) => res.data?.success && setTeams((res.data.teams || []).filter((t: MhlTeam) => t.active)))
            .catch(() => setTeams([]))
    }, [isMhlAdmin])

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            const teamId = isMhlAdmin && viewTeamId !== 'all' ? viewTeamId : undefined
            const withTeam = (data: Record<string, unknown>) => (teamId ? { ...data, teamId } : data)
            const [dash, web, all, list, stats] = await Promise.all([
                api({ url: '/military-hearing-loss-leads', data: withTeam({ source: 'dashboard' }) }),
                api({ url: '/military-hearing-loss-leads', data: withTeam({ source: 'webform' }) }),
                api({ url: '/military-hearing-loss-leads', data: withTeam({}) }),
                api({ url: '/military-hearing-loss-leads-list', data: withTeam({}) }),
                api({ url: '/military-hearing-loss-stats', data: withTeam({ timezone }) }).catch((err) => {
                    // Plain team members can't see stats; the overview itself would have 403'd first.
                    if (err?.response?.status === 403) return { data: { success: true, agents: [] } }
                    throw err
                }),
            ])
            setOverviews({ dashboard: dash.data, webform: web.data, all: all.data })
            if (list.data?.success) setLeads(list.data.leads || [])
            if (stats.data?.success) setAgentStats(stats.data.agents || stats.data.ats || [])
            setForbidden(false)
        } catch (err: any) {
            if (err?.response?.status === 403) {
                setForbidden(true)
                return
            }
            setError(err?.response?.data?.message || err?.message || 'Failed to load overview data')
        } finally {
            setLoading(false)
        }
    }, [isMhlAdmin, viewTeamId])

    // Scoped users only receive dashboard-onboarded leads, so keep them on that tab.
    useEffect(() => {
        if (scoped) setTab('dashboard')
    }, [scoped])

    useEffect(() => {
        setLeadFilters(MHL_LEAD_FILTER_DEFAULTS)
    }, [tab])

    useEffect(() => {
        void load()
    }, [load])

    const webformLeads = useMemo(
        () =>
            leads.filter(
                (l) =>
                    l.submitted_via !== 'dashboard' &&
                    (!WEBFORM_COHORT_FILTER_ENABLED || new Date(l.submitted_at) >= startOfToday()),
            ),
        [leads],
    )

    const tabLeads = useMemo(
        () =>
            tab === 'all'
                ? leads
                : tab === 'dashboard'
                  ? leads.filter((l) => l.submitted_via === 'dashboard')
                  : webformLeads,
        [leads, webformLeads, tab],
    )

    const filteredTabLeads = useMemo(
        () => filterAndSortMhlLeads(tabLeads, leadFilters),
        [tabLeads, leadFilters],
    )

    const channelOptions = useMemo(() => mhlChannelOptions(tabLeads, tab !== 'dashboard'), [tabLeads, tab])

    const overview = overviews[tab]
    // The webform cohort is filtered in the browser, so its figures are too.
    const summary = tab === 'webform' ? summariseLeads(webformLeads) : overview?.summary
    const chart = useMemo(
        () => restyle(chartFromLeads(tabLeads, Number(chartRange))),
        [tabLeads, chartRange],
    )
    const activeAgents = useMemo(() => agentStats.filter((a) => a.leadsSubmittedTotal > 0), [agentStats])

    const filteredAgents = useMemo(() => {
        const q = agentSearch.trim().toLowerCase()
        const list = activeAgents.filter(
            (a) => !q || [a.agent, a.email].some((v) => (v || '').toLowerCase().includes(q)),
        )
        const sorted = [...list]
        sorted.sort((a, b) => {
            switch (agentSort) {
                case 'today_desc':
                    return b.leadsSubmittedToday - a.leadsSubmittedToday || a.agent.localeCompare(b.agent)
                case 'name_asc':
                    return a.agent.localeCompare(b.agent)
                case 'signed_desc':
                    return b.signed - a.signed || a.agent.localeCompare(b.agent)
                case 'total_desc':
                default:
                    return b.leadsSubmittedTotal - a.leadsSubmittedTotal || a.agent.localeCompare(b.agent)
            }
        })
        return sorted
    }, [activeAgents, agentSearch, agentSort])

    if (forbidden || (!isMhlAdmin && !scopeLoading && scope && !scope.canViewStats)) {
        return (
            <div className={styles.wrap}>
                <div className={styles.emptyState}>
                    <p style={{ margin: 0, fontWeight: 600, color: colors.ink }}>The overview isn’t available to you</p>
                    <p className={styles.hint} style={{ marginTop: 6 }}>
                        Team leaders see an overview of their team; MHL admins and Sentinel see everything.
                    </p>
                </div>
            </div>
        )
    }

    const scopeHint = scoped
        ? scope?.teamName
            ? `Your team, ${scope.teamName}: clients onboarded through the dashboard by you and your team-mates.`
            : 'Clients you have onboarded through the dashboard.'
        : teamView
          ? `Team view — ${teamView.name}: only clients onboarded by this team's agents.`
          : null

    return (
        <div className={styles.wrap}>
            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.hint}>{scopeHint ?? TAB_HINT[tab]}</p>
                </div>
                <div className={styles.headerActions}>
                    {isMhlAdmin && teams.length > 0 && (
                        <select
                            className={styles.select}
                            value={viewTeamId === 'all' ? 'all' : String(viewTeamId)}
                            onChange={(e) => {
                                const v = e.target.value
                                setViewTeamId(v === 'all' ? 'all' : Number(v))
                                if (v !== 'all' && tab === 'webform') setTab('dashboard')
                            }}
                            aria-label="Overview view"
                            title="Switch between all agents and a single team"
                        >
                            <option value="all">View: all agents</option>
                            {teams.map((t) => (
                                <option key={t.id} value={t.id}>
                                    Team: {t.name}
                                </option>
                            ))}
                        </select>
                    )}
                    <button type="button" className={styles.btn} onClick={() => void load()} disabled={loading}>
                        <FaSyncAlt size={11} /> Refresh
                    </button>
                </div>
            </header>

            {scoped || teamView ? null : (
                <SourceTabs
                    ariaLabel="Lead group"
                    value={tab}
                    onChange={setTab}
                    options={[
                        { value: 'all', label: 'All leads', count: overviews.all?.summary.leadsTotal ?? null },
                        {
                            value: 'dashboard',
                            label: 'Onboarded via dashboard',
                            count: overviews.dashboard?.summary.leadsTotal ?? null,
                        },
                        {
                            value: 'webform',
                            label: 'Onboarded via webform',
                            count: webformLeads.length,
                        },
                    ]}
                />
            )}

            {error && <div className={styles.errorBanner}>{error}</div>}

            {loading && !summary ? (
                <div className={styles.emptyState}>Loading…</div>
            ) : summary ? (
                <>
                    <div className={styles.statGrid}>
                        <StatTile label="Submitted leads" value={summary.leadsTotal} accent />
                        <StatTile label="Submitted today" value={summary.leadsTotalToday} />
                        <StatTile label="Above quantum" value={summary.aboveQuantumTotal} tone="good" />
                        <StatTile
                            label="Below quantum"
                            value={summary.belowQuantumTotal}
                            tone={summary.belowQuantumTotal ? 'bad' : undefined}
                        />
                        <StatTile
                            label="Quantum TBC"
                            value={summary.tbcQuantumTotal}
                            tone={summary.tbcQuantumTotal ? 'warn' : undefined}
                        />
                        {tab === 'dashboard' && (
                            <>
                                <StatTile
                                    label="Awaiting review"
                                    value={summary.awaitingReview}
                                    tone={summary.awaitingReview ? 'warn' : undefined}
                                    onClick={() => navigate('/military-hearing-loss/leads?review=awaiting_review')}
                                />
                                <StatTile label="Approved" value={summary.approved} tone="good" />
                                <StatTile
                                    label="Rejected"
                                    value={summary.rejected}
                                    tone={summary.rejected ? 'bad' : undefined}
                                />
                            </>
                        )}
                        <StatTile label="E-sign links sent" value={summary.cfaSent} />
                        <StatTile
                            label="CFA signed"
                            value={summary.cfaSigned}
                            tone="good"
                            note={summary.cfaSignedToday ? `${summary.cfaSignedToday} today` : undefined}
                        />
                        <StatTile
                            label="Conversion"
                            value={
                                summary.cfaSent ? `${Math.round((summary.cfaSigned / summary.cfaSent) * 100)}%` : '—'
                            }
                            note="signed ÷ links sent"
                        />
                    </div>

                    <CollapsibleSection title="Leads vs signed" flush defaultOpen>
                        <div className={styles.chartRangeToggle}>
                            <ToggleInput
                                value={chartRange}
                                options={CHART_RANGE_OPTIONS}
                                onChange={setChartRange}
                                ariaLabel="Chart period"
                                fit
                            />
                        </div>
                        <div className={styles.chartPanel}>
                            {chart ? (
                                <div style={{ height: 280 }}>
                                    <Line options={CHART_OPTIONS} data={chart} />
                                </div>
                            ) : (
                                <p className={styles.emptyStateSmall}>No chart data.</p>
                            )}
                        </div>
                    </CollapsibleSection>

                    {tab !== 'webform' && (isMhlAdmin || scope?.canViewStats) && (
                        <CollapsibleSection
                            flush
                            title={
                                teamView
                                    ? `Volume by agent — ${teamView.name}`
                                    : scoped && scope?.teamName
                                      ? `Volume by agent — ${scope.teamName}`
                                      : 'Volume by agent'
                            }
                            count={filteredAgents.length}
                            hint="Which agents are supplying clients into the system. Counts use the agent's Cognito identity so renames don't split their history."
                            actions={
                                (isMhlAdmin || scope?.canViewStats) && (
                                    <button
                                        type="button"
                                        className={`${styles.btn} ${styles.btnSmall}`}
                                        onClick={() => navigate('/military-hearing-loss/admin')}
                                    >
                                        Full agent stats
                                    </button>
                                )
                            }
                            defaultOpen
                        >
                            {activeAgents.length ? (
                                <>
                                    <div className={styles.toolbar}>
                                        <div className={styles.searchWrap}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                className={styles.searchInput}
                                                placeholder="Search agent or email…"
                                                value={agentSearch}
                                                onChange={(e) => setAgentSearch(e.target.value)}
                                            />
                                        </div>
                                        <select
                                            className={styles.select}
                                            value={agentSort}
                                            onChange={(e) => setAgentSort(e.target.value as AgentSort)}
                                            aria-label="Sort agents"
                                        >
                                            <option value="total_desc">Sort: total high → low</option>
                                            <option value="today_desc">Sort: today high → low</option>
                                            <option value="signed_desc">Sort: signed high → low</option>
                                            <option value="name_asc">Sort: name A → Z</option>
                                        </select>
                                        <span className={styles.resultCount}>
                                            {filteredAgents.length} of {activeAgents.length}
                                        </span>
                                    </div>
                                    <div className={styles.tableWrap} style={{ borderRadius: 8 }}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Agent</th>
                                                <th className={styles.num}>Today</th>
                                                <th className={styles.num}>7 days</th>
                                                <th className={styles.num}>30 days</th>
                                                <th className={styles.num}>Total</th>
                                                <th className={styles.num}>Awaiting</th>
                                                <th className={styles.num}>Approved</th>
                                                <th className={styles.num}>Signed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAgents.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} className={styles.emptyState}>
                                                        No agents match the current filters.
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredAgents.map((a) => (
                                                <tr key={a.agentKey}>
                                                    <td>
                                                        <div className={styles.stackCell}>
                                                            <span className={styles.stackPrimary}>{a.agent}</span>
                                                            <span className={styles.stackSecondary}>{a.email}</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.num}>{a.leadsSubmittedToday}</td>
                                                    <td className={styles.num}>{a.leadsSubmitted7d}</td>
                                                    <td className={styles.num}>{a.leadsSubmitted30d}</td>
                                                    <td className={styles.num}>
                                                        <strong>{a.leadsSubmittedTotal}</strong>
                                                    </td>
                                                    <td className={styles.num}>{a.awaitingReview}</td>
                                                    <td className={styles.num}>{a.approved}</td>
                                                    <td className={styles.num}>{a.signed}</td>
                                                </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                </>
                            ) : (
                                <p className={styles.emptyStateSmall}>No agent has submitted a client yet.</p>
                            )}
                        </CollapsibleSection>
                    )}

                    <CollapsibleSection flush title="Leads" count={filteredTabLeads.length} defaultOpen>
                        <MhlLeadsFilter
                            filters={leadFilters}
                            onChange={setLeadFilters}
                            matched={filteredTabLeads.length}
                            total={tabLeads.length}
                            showReview={tab !== 'webform'}
                            channelOptions={channelOptions}
                        />
                        <LeadsTable
                            leads={filteredTabLeads}
                            loading={loading}
                            showReview={tab !== 'webform'}
                            compact
                            pageSize={15}
                            emptyMessage={
                                tabLeads.length === 0
                                    ? 'No leads in this group yet.'
                                    : 'No leads match the current filters.'
                            }
                        />
                    </CollapsibleSection>
                </>
            ) : null}
        </div>
    )
}
